import { Injectable, OnDestroy } from "@angular/core";
import { Observable, BehaviorSubject, Subscription } from "rxjs";
import { modosOrdenacao } from "src/app/constants/modosOrdenacao";
import { EnumTipoOrdenacaoNovaGuia } from "src/app/enums/EnumTipoOrdenacaoNovaGuia";
import { StringHelper } from "src/app/helpers/string.helper";
import {
  ItemLookupOrdenacaoNovaGuia,
} from "src/app/models/lei/item.lookup.ordenacao.nova.guia";
import { LeiLookup } from "src/app/models/lei/lei.lookup";
import { EstatisticasLeitura } from "src/app/models/usuario/EstatisticasLeitura";
import { LeiRepositorio } from "src/app/repositorios/lei.repositorio";
import { UsuarioEstatisticasService } from "src/app/services/data-services/usuario.estatisticas.service";
import { UsuarioGuiasService } from "src/app/services/data-services/usuario.guias.service";
import { UiService } from "src/app/services/ui.service";

@Injectable({
  providedIn: "root",
})
export class NavegacaoPesquisaLeisService implements OnDestroy {
  private leisLookupFiltrada: LeiLookup[] = [];
  private leisLookup: LeiLookup[] = [];
  private leisLookup$ = new BehaviorSubject<LeiLookup[]>(this.leisLookup);

  private filtrarFavoritos = false;
  private filtrarFavoritos$ = new BehaviorSubject<boolean>(
    this.filtrarFavoritos
  );

  private textoBusca = "";
  private textoBusca$ = new BehaviorSubject<string>(this.textoBusca);

  private pagina: Set<number> = new Set<number>();
  private pagina$ = new BehaviorSubject<Set<number>>(this.pagina);

  private paginaPesquisa: Set<number> = new Set<number>();
  private paginaPesquisa$ = new BehaviorSubject<Set<number>>(
    this.paginaPesquisa
  );

  private exibirNavegacaoPesquisaMobile = false;
  private exibirNavegacaoPesquisaMobile$ = new BehaviorSubject<boolean>(
    this.exibirNavegacaoPesquisaMobile
  );

  private modoOrdenacao: ItemLookupOrdenacaoNovaGuia = modosOrdenacao[3];
  private modoOrdenacao$ = new BehaviorSubject<ItemLookupOrdenacaoNovaGuia>(
    this.modoOrdenacao
  );

  private eventosLoading: string[] = [];
  private eventosLoading$ = new BehaviorSubject<boolean>(
    this.eventosLoading.length > 0
  );

  private recentSearches: string[] = [];
  private recentSearches$ = new BehaviorSubject<string[]>(this.recentSearches);

  private subscriptions: Subscription[] = [];

  constructor(
    private leiRepositorio: LeiRepositorio,
    private usuarioEstatisticasService: UsuarioEstatisticasService,
    private usuarioGuiasService: UsuarioGuiasService,
    public uiService: UiService
  ) {
    this.leisPaginada();

    // Sempre que guia é alterada, é alterada duas vezes
    this.subscriptions.push(
      this.usuarioGuiasService
        .getGuiaAtiva()
        .subscribe(() => this.guiaAtivaChanged())
    );
    this.subscriptions.push(
      this.usuarioEstatisticasService
        .getLeisFavoritas()
        .subscribe((fav) => this.favoritosChanged(fav))
    );
    this.subscriptions.push(
      this.uiService.getMobile().subscribe((mobile) => {
        if (this.exibirNavegacaoPesquisaMobile && !mobile) {
          this.updateExibirNavegacaoPesquisaMobile();
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];
  }

  public async leisPaginada() {
    if (!this.pagina.has(-1) && this.pagina.has(this.pagina.size)) {
      return;
    }
    this.addProximaPagin();
    const lei = await this.leiRepositorio.carregarLeisPaginada(
      this.pagina.size - 1
    );

    if (!lei) {
      return;
    }

    if (lei.length === 0) {
      this.pagina.add(-1);
      this.updatePagina();
    }

    this.leisLookup = Object.values(
      [...this.leisLookup, ...lei].reduce(
        (acc, obj) => ({ ...acc, [obj.id]: obj }),
        {}
      )
    );
    this.carregarLeis();
  }

  public async leisPesquisaPaginada() {
    if (
      !this.pagina.has(-1) &&
      this.paginaPesquisa.has(this.paginaPesquisa.size)
    ) {
      return;
    }
    this.addProximaPaginPesquisa();
    const lei = await this.leiRepositorio.carregarLeisPesquisaPaginada(
      this.paginaPesquisa.size - 1,
      this.textoBusca
    );
    if (lei.length === 0) {
      this.paginaPesquisa.add(-1);
      this.updatePaginaPesquisa();
    }
    this.leisLookup = Object.values(
      [...this.leisLookup, ...lei].reduce(
        (acc, obj) => ({ ...acc, [obj.id]: obj }),
        {}
      )
    );

    this.carregarLeis();
  }

  public getValuePagina(): Set<number> {
    return this.pagina;
  }

  public async carregarLeis() {
    const promises = new Array<Promise<void>>();
    const estatisticas = await this.usuarioEstatisticasService.listar();
    for (const lei of this.leisLookup) {
      lei.estatisticas = estatisticas.find((e) => e.id === lei.id);
      if (!lei.estatisticas) {
        const estatistica = new EstatisticasLeitura();
        estatistica.id = lei.id.toString();
        promises.push( this.usuarioEstatisticasService.atualizar(estatistica));
        lei.estatisticas = estatistica;
      }

      lei.progresso = EstatisticasLeitura.CalcularProgresso(
        lei.quantidadeItens,
        lei.estatisticas.linhasLidas.length
      );
      lei.favorita = lei.estatisticas.favorito;
    }
    await Promise.all(promises);
    this.aplicarFiltro();
  }

  public aplicarFiltro() {
    this.leisLookupFiltrada = this.leisLookup;

    if (!this.leisLookupFiltrada || this.leisLookupFiltrada.length === 0) {
      return;
    }

    if (this.filtrarFavoritos) {
      this.leisLookupFiltrada = this.leisLookupFiltrada.filter(
        (l) => l.favorita
      );
    }

    if (this.textoBusca) {
      const query = StringHelper.RemoverAcentosCaracteresEspeciais(
        this.textoBusca
      ).toLowerCase();
      const words = query.split(" ");

      this.leisLookupFiltrada = this.leisLookupFiltrada.filter((l) => {
        let matches = 0;

        words.forEach((word) => {
          const tituloLei = StringHelper.RemoverAcentosCaracteresEspeciais(
            l.titulo
          ).toLowerCase();
          if (tituloLei.indexOf(word) !== -1) {
            matches++;
          }
        });

        return matches === words.length;
      });
    }

    switch (this.modoOrdenacao.ordenacao) {
      case EnumTipoOrdenacaoNovaGuia.OrdemAlfabetica:
        this.leisLookupFiltrada = this.leisLookupFiltrada.sort((a, b) =>
          a.titulo.localeCompare(b.titulo)
        );
        break;
      case EnumTipoOrdenacaoNovaGuia.MaisLidos:
        this.leisLookupFiltrada = this.leisLookupFiltrada.sort(
          (a1, a2) => a2.progresso - a1.progresso
        );
        break;
      case EnumTipoOrdenacaoNovaGuia.MenosLidos:
        this.leisLookupFiltrada = this.leisLookupFiltrada.sort(
          (a1, a2) => a1.progresso - a2.progresso
        );
        break;
      case EnumTipoOrdenacaoNovaGuia.DataAlteracao:
        this.leisLookupFiltrada = this.leisLookupFiltrada.sort(
          (a1, a2) =>
            new Date(a2.dataHoraUltimaModificacaoOficial).getTime() -
            new Date(a1.dataHoraUltimaModificacaoOficial).getTime()
        );
        break;
    }

    this.updateLeisLookup();
  }

  public  alterarModoOrdenacao(novoModo: ItemLookupOrdenacaoNovaGuia) {
    const nomeEvento = "modoOrdenacao" + Date.now().toString();
    this.alterarCarregando(true, nomeEvento);

    this.modoOrdenacao = novoModo;
    this.updateModoOrdenacao();
    this.aplicarFiltro();

    this.alterarCarregando(false, nomeEvento);
  }

  public  favoritosToggle() {
    const nomeEvento = "favoritos_togle" + Date.now().toString();
    this.alterarCarregando(true, nomeEvento);

    this.filtrarFavoritos = !this.filtrarFavoritos;

    this.updateFiltrarFavoritos();

    this.aplicarFiltro();

    this.alterarCarregando(false, nomeEvento);
  }

  public  alterarTextoBusca(txtBusca: string) {
    const nomeEvento = "textoBusca" + Date.now().toString();

    this.alterarCarregando(true, nomeEvento);
    if (txtBusca.length === 0 || txtBusca.length < 3) {
      this.paginaPesquisa.clear();
      this.updatePaginaPesquisa();
    }

    this.textoBusca = txtBusca;
    this.updateTextoBusca();

    if (txtBusca.length >= 3) {
      // this.addToRecentSearche(txtBusca);
      this.leisPesquisaPaginada();
    }
    this.aplicarFiltro();

    this.alterarCarregando(false, nomeEvento);
  }

  public favoritosChanged(fav: string[]): void {
    const nomeEvento = "favoritosChange" + Date.now().toString();
    this.alterarCarregando(true, nomeEvento);
    this.leisLookup.forEach((lei) => {
      lei.favorita = fav.indexOf(lei.id) !== -1;
    });

    this.aplicarFiltro();

    this.alterarCarregando(false, nomeEvento);
  }

  public guiaAtivaChanged(): void {
    const nomeEvento = "guiaChange" + Date.now().toString();
    this.alterarCarregando(true, nomeEvento);

    this.alterarCarregando(false, nomeEvento);
  }

  public addToRecentSearche(term: string) {
    const lowerCaseTerm = term.toLowerCase();

    if (this.recentSearches.length >= 3) {
      this.recentSearches.shift();
    }
    this.recentSearches = [
      lowerCaseTerm,
      ...this.recentSearches.filter((s) => s !== lowerCaseTerm),
    ];
    this.updateRecentSearches();
  }

  public removeRecentSearche(term: string) {
    const lowerCaseTerm = term.toLowerCase();

    this.recentSearches = [
      ...this.recentSearches.filter((s) => s !== lowerCaseTerm),
    ];
    this.updateRecentSearches();
  }

  public updateLeisLookup() {
    this.leisLookup$.next(this.leisLookupFiltrada);
  }

  public getLeisLookup(): Observable<LeiLookup[]> {
    return this.leisLookup$.asObservable();
  }

  public updateFiltrarFavoritos() {
    this.filtrarFavoritos$.next(this.filtrarFavoritos);
  }

  public getFiltrarFavoritos(): Observable<boolean> {
    return this.filtrarFavoritos$.asObservable();
  }

  public updateTextoBusca() {
    this.textoBusca$.next(this.textoBusca);
  }

  public getTextoBusca(): Observable<string> {
    return this.textoBusca$.asObservable();
  }

  public updateModoOrdenacao() {
    this.modoOrdenacao$.next(this.modoOrdenacao);
  }

  public getModoOrdenacao(): Observable<ItemLookupOrdenacaoNovaGuia> {
    return this.modoOrdenacao$.asObservable();
  }

  public updateEventosLoading() {
    this.eventosLoading$.next(this.eventosLoading.length > 0);
  }

  public getEventosLoading(): Observable<boolean> {
    return this.eventosLoading$.asObservable();
  }
  public updatePagina() {
    this.pagina$.next(this.pagina);
  }

  public getPagina(): Observable<Set<number>> {
    return this.pagina$.asObservable();
  }

  public addProximaPagin() {
    if (this.pagina.size === 0) {
      this.pagina.add(0);
    } else {
      this.pagina.add(this.pagina.size);
    }

    this.updatePagina();
  }

  public updatePaginaPesquisa() {
    this.paginaPesquisa$.next(this.paginaPesquisa);
  }

  public getPaginaPesquisa(): Observable<Set<number>> {
    return this.paginaPesquisa$.asObservable();
  }

  public updateRecentSearches() {
    this.recentSearches$.next(this.recentSearches);
  }

  public getRecentSearches(): Observable<string[]> {
    return this.recentSearches$.asObservable();
  }

  public addProximaPaginPesquisa() {
    if (this.paginaPesquisa.size === 0) {
      this.paginaPesquisa.add(0);
    } else {
      this.paginaPesquisa.add(this.paginaPesquisa.size);
    }
    this.updatePaginaPesquisa();
  }

  public updateExibirNavegacaoPesquisaMobile() {
    this.exibirNavegacaoPesquisaMobile = !this.exibirNavegacaoPesquisaMobile;
    this.exibirNavegacaoPesquisaMobile$.next(
      this.exibirNavegacaoPesquisaMobile
    );
  }

  public getExibirNavegacaoPesquisaMobile(): Observable<boolean> {
    return this.exibirNavegacaoPesquisaMobile$.asObservable();
  }
  private alterarCarregando(carregando, nomeEvento) {
    if (carregando) {
      this.eventosLoading = this.eventosLoading
        ? this.eventosLoading
        : new Array<string>();
      this.eventosLoading.push(nomeEvento);
      this.updateEventosLoading();
    } else {
      const i = this.eventosLoading.findIndex((x) => x === nomeEvento);
      this.eventosLoading.splice(i, 1);
      this.updateEventosLoading();
    }
  }
}
